import React, { useEffect, useState } from 'react'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom'
import { Security } from '@okta/okta-react'
import { OktaAuth } from '@okta/okta-auth-js'
import { ThemeProvider } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { api, isOktaDown, oidcConfig, routes } from 'config'
import {
  BaseLayout,
  BlankLayout,
  CustomAlert,
  CustomerSupportNumber,
  ErrorBoundary,
  Loader,
} from 'components'
import {
  ChangePassword,
  Enroll,
  ForgotLoginName,
  ForgotPassword,
  GatewayCallback,
  Home,
  Impersonate,
  ImpersonateGateway,
  ImplicitCallback,
  Login,
  Logout,
  MembershipExpired,
  MFA,
  NoAccess,
  PageNotFound,
  PasswordExpired,
  ResetPassword,
  SubscriptionAgreement,
  ExpirePassword,
  UpdateEnrollment,
  UpdateEnrollmentOtpForm,
} from 'pages/'
import { constants } from 'utils'
import './scss/fontFamily.scss'
import './scss/card.scss'
import './scss/alertBox.scss'
import theme from './theme/'
import axios from 'axios'
import PasswordAndSecurity from 'pages/passwords-security'
import { RequireAuth } from 'RequireAuth'

export const AppRouter = () => {
  //const navigate = useNavigate()
  const [articleAPIComplete, setArticleAPIStatus] = useState<any | null>(null)
  const [oktaHealthStatus, setOktaHealthStatus] = useState(
    constants.OKTA_HEALTH_STATUS.PENDING,
  )
  const location = useLocation()
  const getData = async () => {
    const URL =
      location.pathname === '/mfa' ? api.mfaArticle() : api.loginArticle()
    axios
      .get(URL, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      })
      .then((response) => {
        location.pathname === '/mfa'
          ? sessionStorage.setItem('mfa_article', JSON.stringify(response.data))
          : sessionStorage.setItem(
              'login_article',
              JSON.stringify(response.data),
            )
        setArticleAPIStatus(true)
      })
      .catch(() => {
        axios
          .get(api.loginArticleSecondary(), {
            headers: {
              'Content-Type': 'application/json',
            },
            withCredentials: false,
          })
          .then((response) => {
            sessionStorage.setItem(
              'login_article',
              JSON.stringify(response.data),
            )
            setArticleAPIStatus(true)
          })
          .catch(() => {
            setArticleAPIStatus(true)
          })
      })
  }
  useEffect(() => {
    isOktaDown().then((response: boolean) =>
      setOktaHealthStatus(
        response
          ? constants.OKTA_HEALTH_STATUS.DOWN
          : constants.OKTA_HEALTH_STATUS.UP,
      ),
    )
    if (process.env.REACT_APP_IMPERSONATE_LOGIN !== 'true') {
      getData()
    } else {
      setArticleAPIStatus(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  if (oktaHealthStatus === constants.OKTA_HEALTH_STATUS.PENDING) {
    return (
      <BlankLayout>
        <Loader />
      </BlankLayout>
    )
  }

  if (oktaHealthStatus === constants.OKTA_HEALTH_STATUS.DOWN) {
    return (
      <BaseLayout>
        <CustomAlert severity="error" title="Something went wrong!">
          A problem occurred with your request. Please try again in a few
          minutes, or call customer support for further assistance at{' '}
          <CustomerSupportNumber />.
        </CustomAlert>
      </BaseLayout>
    )
  }

  const oktaAuth = new OktaAuth(oidcConfig)

  return (
    articleAPIComplete && (
      <ErrorBoundary>
        <Security
          oktaAuth={oktaAuth}
          restoreOriginalUri={(oktaAuth: any, originalUri: any) =>
            window.location.replace(originalUri || window.location.origin)
          }
        >
          <Routes>
            <Route path={routes.home} element={<RequireAuth />}>
              <Route path="" element={<Home />} />
            </Route>
            <Route path={routes.welcome} element={<ResetPassword />} />
            <Route path={routes.login} element={<Login />} />
            <Route path={routes.enroll} element={<Enroll />} />
            <Route path={routes.loginCallback} element={<ImplicitCallback />} />
            <Route path={routes.changePassword} element={<RequireAuth />}>
              <Route path="" element={<ChangePassword />} />
            </Route>
            <Route
              path={routes.passwordExpired}
              element={<PasswordExpired />}
            />
            <Route
              path={routes.gatewayCallback}
              element={<GatewayCallback />}
            />
            <Route path={routes.mfa} element={<MFA />} />
            <Route path={routes.updateEnrollment} element={<RequireAuth />}>
              <Route path="" element={<UpdateEnrollment />} />
            </Route>
            <Route
              path={routes.updateEnrollmentOtpForm}
              element={<RequireAuth />}
            >
              <Route path="" element={<UpdateEnrollmentOtpForm />} />
            </Route>
            <Route
              path={routes.membershipExpired}
              element={<MembershipExpired />}
            />
            <Route
              path={routes.subscriptionAgreement}
              element={<RequireAuth />}
            >
              <Route path="" element={<SubscriptionAgreement />} />
            </Route>
            <Route
              path={routes.forgotLoginName}
              element={<ForgotLoginName />}
            />
            <Route path={routes.forgotPassword} element={<ForgotPassword />} />
            <Route path={routes.resetPassword} element={<ResetPassword />} />
            <Route path={routes.logout} element={<Logout />} />
            <Route path={routes.impersonate} element={<RequireAuth />}>
              <Route path="login" element={<Impersonate />} />
            </Route>
            <Route
              path={routes.impersonateGateway}
              element={<ImpersonateGateway />}
            />
            <Route path={routes.noAccess} element={<NoAccess />} />
            <Route path={routes.expirePassword} element={<RequireAuth />}>
              <Route path="" element={<ExpirePassword />} />
            </Route>
            <Route path={routes.passwordSecurity} element={<RequireAuth />}>
              <Route path="" element={<PasswordAndSecurity />} />
            </Route>
            <Route element={<PageNotFound />} />
          </Routes>
        </Security>
      </ErrorBoundary>
    )
  )
}

const App = () => (
  <ThemeProvider theme={theme}>
    <Router>
      <CssBaseline />
      <AppRouter />
    </Router>
  </ThemeProvider>
)

export default App
